import {
  AvailableNumberCountryCode,
  AvailableNumberOutSchema,
  NumberProviderInSchema,
  PreSaleBusinessInformationInSchema,
  SignUpFlow,
  SubscriptionPlanOutSchema,
  SubscriptionTerm,
  UserWithPasswordInSchema,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import { getAvailableNumbers } from "@/api/requests/availableNumber";
import {
  submitRecommendationForm,
  updateRecommendationForm,
} from "@/api/requests/onboarding";
import { getUnprovisionedPayment } from "@/api/requests/payments";
import {
  getSubscriptionPlan,
  webPaymentPurchase,
  webPaymentPurchaseV2,
} from "@/api/requests/purchase";
import { CustomAxiosRequestConfig } from "@/api/types";
import useAnalytics from "@/composables/useAnalytics";
import { findRecommendedPlan, validateAuthInput } from "@/helpers/auth";
import { PAYSTACK_KEY, PINIA_PERSIST_OPTIONS } from "@/helpers/constants";
import {
  recommendationDataEventMap,
  trackAmplitudeEvent,
} from "@/helpers/integrations/analytics/amplitude";
import { AMPLITUDE_EVENTS } from "@/helpers/integrations/analytics/events";
import { trackFacebookPurchaseEvent } from "@/helpers/integrations/analytics/facebook-pixels";
import logger from "@/helpers/logger";
import {
  campaignPurchase,
  sendCampaignUTMData,
  sendRecommendation,
  sendSignupPurchaseInvoice,
} from "@/helpers/queries";
import { WebPurchaseData, WebPurchaseDataV2 } from "@/helpers/types/global";
import { CampaignUtmRequest } from "@/helpers/types/route";
import router from "@/router";
import { notify } from "@kyvg/vue3-notification";
import Paystack from "@paystack/inline-js";
import { defineStore } from "pinia";
import { useAuthStore } from "./auth.store";
import { useUsersStore } from "./users.store";
import { useUtilsStore } from "./utils.store";
import { useGoogleAds } from "@/composables/useGoogleAds";

export type SelectNumber = AvailableNumberOutSchema & { label?: string };

interface signup {
  businessName?: string | null;
  country?: string | null;
  role?: string | null;
  industry?: string | null;
  hearAboutUs?: string | null;
  otherSource?: string | null;
  promo_code?: string | null;
  recommended_plan?: SubscriptionPlanOutSchema | null;
  tabs: { title: string }[];
  numberOfReceivers?: string | number | null;
  getInternationalNumber: boolean;
  activeTab: number;
  plans: SubscriptionPlanOutSchema[];
  selectedTimeDuration: unknown;
  otherRole?: string | null;
  selectedNumber?: SelectNumber | null;
  hasCoupon: boolean;
  isLoading: boolean;
  isFetchingAmount: boolean;
  invoiceDetails?: {
    fullName: string;
    email: string;
    phoneNumber: string;
  } | null;
  skipPaystack: boolean;
  showInvoiceSentModal: boolean;
  requestedSpecialNumber: boolean;
  hasClickedPurchase: boolean;
  showInvoiceModal: boolean;
  showPricingDrawer: boolean;
  claimedNumber: boolean;
  completedStep: number;
  is_available_number_changed?: boolean | null;
  preSaleId?: number | null;
  available_number?: string | null;
  isFetchingUserInfo: boolean;
  campaignHashId: string;
  showSuccessSpecialNumberPurchaseModal: boolean;
  showPendingInvoiceModal: boolean;
  globalId: string;
  flowType: SignUpFlow;
  hasTypeFromUrl: boolean;
  showSuccessModal: boolean;
  campaignUtmParams:
    | {
        utmSource: string;
        utmMedium: string;
        utmCampaign: string;
      }
    | undefined
    | null;
}
export const useSignUpStore = defineStore("signup", {
  state: (): signup => ({
    businessName: null,
    country: "Nigeria",
    role: null,
    industry: null,
    hearAboutUs: null,
    otherSource: null,
    promo_code: null,
    recommended_plan: null,
    numberOfReceivers: 0,
    otherRole: null,

    getInternationalNumber: false,
    plans: [],
    tabs: [
      {
        title: "Monthly",
      },
      {
        title: "Quarterly",
      },
      {
        title: "Annually",
      },
    ],
    activeTab: 0,
    selectedTimeDuration: 0,
    selectedNumber: null,
    hasCoupon: false,
    isLoading: false,
    isFetchingAmount: false,
    invoiceDetails: null,
    skipPaystack: false,
    showInvoiceSentModal: false,
    requestedSpecialNumber: false,
    hasClickedPurchase: false,
    showInvoiceModal: false,
    showPricingDrawer: false,
    claimedNumber: false,
    completedStep: 0,
    is_available_number_changed: false,
    preSaleId: null,
    available_number: null,
    isFetchingUserInfo: false,
    campaignHashId: "",
    showSuccessSpecialNumberPurchaseModal: false,
    showPendingInvoiceModal: false,
    globalId: "",
    flowType: "A",
    hasTypeFromUrl: false,
    showSuccessModal: false,
    campaignUtmParams: null,
  }),
  getters: {
    filterDurationTabs(): { title: string }[] {
      const utilsStore = useUtilsStore();

      if (utilsStore.signInSource === "customer-winback") {
        return [{ title: "Monthly" }];
      } else {
        return this.tabs;
      }
    },

    calculatePrice(): {
      originalPrice: number;
      discountedPrice: number;
      monthlyPrice: number;
    } {
      if (!this.recommended_plan?.standard_cost) {
        logger.error(new Error("No recommended plan found"));

        return {
          originalPrice: 0,
          discountedPrice: 0,
          monthlyPrice: 0,
        };
      }

      let originalPrice = 0;

      const discountedPrice = this.recommended_plan?.standard_cost;

      let monthlyPrice = this.recommended_plan?.standard_cost;

      if (this.activeTab === 1) {
        // quarterly
        originalPrice = (100 / 85) * this.recommended_plan?.standard_cost;
        monthlyPrice = this.recommended_plan?.standard_cost / 3;
      }

      if (this.activeTab === 2) {
        // yearly
        originalPrice = (100 / 80) * this.recommended_plan?.standard_cost;
        monthlyPrice = this.recommended_plan?.standard_cost / 12;
      }

      return {
        originalPrice,
        discountedPrice,
        monthlyPrice,
      };
    },
    canSubmitRecommendationForm(): boolean {
      const referral_source =
        this.hearAboutUs?.toLowerCase() === "others"
          ? this.otherSource?.trim() || null
          : this.hearAboutUs?.trim() || null;

      const job_title =
        this.role?.toLowerCase() === "others"
          ? this.otherRole?.trim() || ""
          : this.role?.trim() || "";

      return (
        validateAuthInput(this.businessName ?? "") &&
        validateAuthInput(this.role ?? "") &&
        !!this.industry &&
        !!this.numberOfReceivers &&
        !!referral_source &&
        !!job_title
      );
    },
    getRecommendationData(): PreSaleBusinessInformationInSchema {
      const userStore = useUsersStore();

      // The user information like name, email is populated only when the user has a business
      // So we need to merge the user information from the current user business level and the register user
      const userData = {
        ...userStore.currentUserBusinessLevel,
        ...userStore.registerUser,
      };

      const userEmail =
        "personal_email" in userData
          ? userData.personal_email
          : "email" in userData
            ? userData.email
            : "";

      const recommendationData: PreSaleBusinessInformationInSchema = {
        user_id: userData?.id,
        first_name: userData?.first_name ?? "",
        last_name: userData?.last_name ?? "",
        email: userEmail ?? "",
        mobile: userData?.mobile ?? "",
        team_size: this.numberOfReceivers ? Number(this.numberOfReceivers) : 0,
        sector: this.industry ?? "",
        plan_id: this.recommended_plan?.id ?? 0,
        business_name: this.businessName ?? "",
        job_title:
          this.role?.toLowerCase() === "others"
            ? this.otherRole?.trim() || null
            : this.role?.trim() || null,
        address: this.country ?? "",
        requires_usa_number: this.getInternationalNumber,
        makes_international_calls: this.getInternationalNumber,
        referral_source:
          this.hearAboutUs?.toLowerCase() === "others"
            ? this.otherSource?.trim() || null
            : this.hearAboutUs || null,
        signup_flow: this.flowType,
      };

      return recommendationData;
    },
    canMoveToNextStep(): boolean | undefined {
      switch (this.completedStep) {
        case 0:
          return !!this.canSubmitRecommendationForm;
        case 1:
          return !!this.selectedNumber?.phone_number;
        case 2:
          return true;
      }
    },
  },
  actions: {
    findPlan() {
      const userStore = useUsersStore();
      const recommendedPlan = findRecommendedPlan(
        this.numberOfReceivers,
        this.getInternationalNumber,
        this.tabs[this.activeTab].title
      );

      if (this.plans.length > 0) {
        const recommendedPlanToUse = this.plans.find((plan) =>
          recommendedPlan
            ?.toLowerCase()
            .includes(plan.title?.toLowerCase() || "")
        );

        // In staging, annual plan has a different title - Premium Plan
        // So we need to fallback to the plan with the title "Premium Plan"
        if (!recommendedPlanToUse && this.activeTab === 2) {
          this.recommended_plan = this.plans.find(
            (plan) => plan.title?.toLowerCase() === "premium plan"
          );

          this.numberOfReceivers = this.recommended_plan?.extensions_limit ?? 5;
        } else {
          this.recommended_plan = recommendedPlanToUse;
        }

        userStore.recommended_plan = this.recommended_plan;
      }
    },
    async activateTab(index: number) {
      try {
        const userStore = useUsersStore();

        this.isFetchingAmount = true;
        this.activeTab = index;
        userStore.activeTab = index;

        const selectedTab = this.tabs[this.activeTab].title?.toLowerCase();

        const term = (
          selectedTab === "annually" ? "yearly" : selectedTab
        ) as SubscriptionTerm;

        const response = await getSubscriptionPlan({
          planType: "number",
          term,
        });

        this.plans = response.data.data;

        this.findPlan();
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error fetching subscription plan",
        });
      } finally {
        this.isFetchingAmount = false;
      }
    },
    syncWithValueWithUserStore() {
      const userStore = useUsersStore();

      this.businessName = userStore.registerBusiness?.business_name ?? "";
      this.country = userStore.registerBusiness?.address ?? "Nigeria";
      this.role = userStore.registerBusiness?.job_title ?? "";
      this.industry = userStore.registerBusiness?.sector ?? "";
      this.hearAboutUs = userStore.registerBusiness?.referral_source ?? "";
      this.otherSource = userStore.registerBusiness?.otherSource ?? "";
      this.otherRole = userStore.registerBusiness?.otherRole ?? "";
    },
    async handleWinbackSignIn(planId?: string | null) {
      const userStore = useUsersStore();
      const utilsStore = useUtilsStore();
      const authStore = useAuthStore();

      this.isLoading = true;

      try {
        void trackAmplitudeEvent(AMPLITUDE_EVENTS.WINBACK_CAMPAIGN_SIGNIN);

        await sendRecommendation(this.getRecommendationData);
        await campaignPurchase(planId ? { plan_id: planId } : undefined);

        const handleGetPendingNumberPayment = async () => {
          try {
            userStore.user_role = "owner";

            const pendingSetupResponse = await getUnprovisionedPayment({});

            const pendingNumberSetup = pendingSetupResponse.data.data;

            utilsStore.setSetUpPayload(pendingNumberSetup);

            authStore.loading = false;

            notify({
              text: "Successfully Purchased a Business number. Proceed to provision a number.",
              type: "success",
            });

            await router.push("/managers/provision-number");
          } catch (error) {
            handleError({
              error,
              fallbackMessage: "Error getting pending number payment",
            });
          }
        };

        setTimeout(() => {
          void handleGetPendingNumberPayment();
        }, 5000);
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error getting pending number payment",
        });

        if (
          String(error).toLowerCase() ===
          "you are not eligible for the campaign. please proceed to purchase an additional number."
        ) {
          utilsStore.signInSource = "";
        }
      } finally {
        this.isLoading = false;
      }
    },
    async choosePaymentOption() {
      const userStore = useUsersStore();
      const authStore = useAuthStore();
      const utilsStore = useUtilsStore();

      this.isLoading = true;

      this.hasClickedPurchase = true;

      // The user information like name, email is populated only when the user has a business
      // So we need to merge the user information from the current user business level and the register user
      const userData = {
        ...userStore.currentUserBusinessLevel,
        ...userStore.registerUser,
      };

      const userEmail =
        "personal_email" in userData
          ? userData.personal_email
          : "email" in userData
            ? userData.email
            : "";

      try {
        const userMobile = userData?.mobile;
        const selectedNumber = this.selectedNumber?.phone_number;
        const recommendedPlanId = this.recommended_plan?.id;

        const flowNumbers: NumberProviderInSchema[] =
          this.flowType === "A"
            ? [
                {
                  phone_number: selectedNumber ?? "",
                  provider: this.selectedNumber?.provider ?? "",
                  country:
                    (this.selectedNumber
                      ?.country_code as AvailableNumberCountryCode) ?? "NG",
                  label: this.selectedNumber?.label,
                },
              ]
            : []; // for flowType B, we don't need to send numbers

        if (!userMobile || !recommendedPlanId || !userEmail) {
          throw new Error(
            `Missing required fields. User mobile: ${userMobile} or Plan id: ${recommendedPlanId} or User email: ${userEmail}`
          );
        }

        const webPurchaseData: WebPurchaseData = {
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userEmail,
          phone_number: userMobile,
          plan_id: recommendedPlanId,
        };

        if (utilsStore.affiliateLinkRef) {
          // update payload before making request
          webPurchaseData.promotion_code = utilsStore.affiliateLinkRef;
        }

        if (this.hasCoupon) {
          webPurchaseData.coupon = this.promo_code as string;
        } else if (authStore.couponCode) {
          webPurchaseData.coupon = authStore.couponCode;
        }

        const webPurchaseDataV2: WebPurchaseDataV2 = {
          ...webPurchaseData,

          numbers: flowNumbers,

          business: {
            name: this.businessName ?? "",
            is_go_global: false,
          },
        };

        this.hasClickedPurchase = true;

        let response;

        if (this.skipPaystack) {
          const payload = {
            data: webPurchaseData,
            bill_to: this.invoiceDetails?.fullName,
            bill_to_email: this.invoiceDetails?.email,
            bill_to_phone_number: this.invoiceDetails?.phoneNumber,
          };

          await sendSignupPurchaseInvoice(payload);

          void trackAmplitudeEvent(AMPLITUDE_EVENTS.INVOICE_PAYMENT_LINK_SENT, {
            selected_number: this.selectedNumber?.phone_number,
            selected_country_code: this.selectedNumber?.country_code,
            plan: this.recommended_plan?.title,
            amount: this.calculatePrice.discountedPrice.toString(),
            device: "web",
          });

          this.showInvoiceSentModal = true;
        } else {
          if (this.flowType === "B") {
            response = await webPaymentPurchase({
              paymentPageInSchema: webPurchaseData,
            });
          } else {
            response = await webPaymentPurchaseV2({
              numberPurchaseInSchema: webPurchaseDataV2,
            });
          }

          if (!webPurchaseData.email) {
            throw new Error("Email is required to complete payment.");
          }

          void trackAmplitudeEvent(AMPLITUDE_EVENTS.PAYMENT_ATTEMPTED, {
            selected_number: this.selectedNumber?.phone_number,
            selected_country_code: this.selectedNumber?.country_code,
            plan: this.recommended_plan?.title,
            amount: this.calculatePrice.discountedPrice.toString(),
            device: "web",
          });

          this.payWithPaystack(
            webPurchaseData.email,
            this.calculatePrice.discountedPrice,
            response.data.ref
          );
        }
      } catch (error) {
        const handledError = handleError({
          error,
          fallbackMessage: "Error while making payment, please try again",
        });

        void trackAmplitudeEvent(AMPLITUDE_EVENTS.PAYMENT_ATTEMPTED_FAILED, {
          email: userEmail,
          reason: handledError.message,
          selected_number: this.selectedNumber?.phone_number,
          selected_country_code: this.selectedNumber?.country_code,
          plan: this.recommended_plan?.title,
          amount: this.calculatePrice.discountedPrice.toString(),
          device: "web",
        });
      } finally {
        this.isLoading = false;
      }
    },
    payWithPaystack(email: string, amount: number, reference: string) {
      const authStore = useAuthStore();
      const analytics = useAnalytics();

      const businessName = this.businessName;
      const planName = this.recommended_plan?.title;
      const planId = this.recommended_plan?.id;

      void analytics.logEvent(AMPLITUDE_EVENTS.PAYSTACK_PAYMENT_ATTEMPTED, {
        email,
        amount,
        reference,
        business_name: businessName,
        plan_name: planName,
        plan_id: planId,
      });

      // TODO: Get Paystack types
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const popup = new Paystack();

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      popup.checkout({
        key: PAYSTACK_KEY,
        email,
        amount: amount * 100,
        reference,
        onError: async (error: unknown) => {
          const handledError = handleError({
            error,
            fallbackMessage: "Error while making payment, please try again",
          });

          await analytics.logEvent(AMPLITUDE_EVENTS.PAYSTACK_PAYMENT_FAILED, {
            email,
            reason: handledError.message,
            reference,
            amount,
            business_name: businessName,
            plan_name: planName,
            plan_id: planId,
          });
        },
        onSuccess: async () => {
          try {
            void analytics.logEvent(AMPLITUDE_EVENTS.PAYSTACK_PAYMENT_SUCCESS, {
              email,
              amount,
              reference,
              business_name: businessName,
              plan_name: planName,
              plan_id: planId,
            });

            void trackFacebookPurchaseEvent({
              amount,
              currency: "NGN",
            });

            const { trackPurchase } = useGoogleAds();
            void trackPurchase();

            if (this.requestedSpecialNumber) {
              this.showSuccessSpecialNumberPurchaseModal = true;
            } else if (this.flowType === "B" && authStore.isAuthenticated) {
              // show successful modal for users that are logged in to access unprovisioned payment
              this.showSuccessModal = true;
            } else {
              await router.push({
                path: "/payment-successful",
                query: {
                  sign_up_flow: "standard", // flow a
                },
              });
              this.clearStore(); // clear business information after successful payment
            }
          } catch (error) {
            handleError({
              error,
              fallbackMessage: "Error while sending payment success event",
              silent: true,
            });
          }
        },

        onCancel: async () => {
          await analytics.logEvent(
            AMPLITUDE_EVENTS.PAYSTACK_PAYMENT_CANCELLED,
            {
              email,
              amount,
              reference,
              business_name: businessName,
              plan_name: planName,
              plan_id: planId,
            }
          );
        },
      });
    },
    async handleSendCampaignData(userData: UserWithPasswordInSchema) {
      try {
        if (this.campaignUtmParams) {
          // Send utm source data (e.g Facebook Ads) to BE
          const campaignUTMPayload: CampaignUtmRequest = {
            first_name: userData.first_name,
            last_name: userData.last_name,
            email: userData.email,
            mobile: userData.mobile,
            lead_source: this.campaignUtmParams.utmSource,
            medium: this.campaignUtmParams.utmMedium,
            campaign: this.campaignUtmParams.utmCampaign,
          };

          await sendCampaignUTMData(campaignUTMPayload);
        }
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error sending campaign utm data",
          silent: true,
        });
      } finally {
        this.campaignUtmParams = null;
      }
    },
    clearStore() {
      this.businessName = "";
      this.getInternationalNumber = false;
      this.role = "";
      this.hearAboutUs = "";
      this.numberOfReceivers = 0;
      this.country = "";
      this.industry = "";
      this.claimedNumber = false;
      this.completedStep = 0;
      this.selectedNumber = null;
      this.requestedSpecialNumber = false;
      this.showSuccessSpecialNumberPurchaseModal = false;
      this.globalId = "";
      this.preSaleId = undefined;
    },
    async fetchSpecialNumber() {
      const response = await getAvailableNumbers({
        provider: "CDV",
        countryCode: "NG",
        pageSize: 4,
      });

      this.selectedNumber = response.data.data[0];
    },
    async onSubmitBusinessInfo() {
      const userStore = useUsersStore();
      const authStore = useAuthStore();

      try {
        this.isLoading = true;
        userStore.registerBusiness = this.getRecommendationData;

        void trackAmplitudeEvent(AMPLITUDE_EVENTS.HEARD_ABOUT_US, {
          Source:
            this.hearAboutUs?.toLowerCase() === "others"
              ? this.otherSource
              : this.hearAboutUs,
        });

        const recommendationEvent = recommendationDataEventMap[
          this.recommended_plan?.title?.toLowerCase() ?? ""
        ] as string; // gets event from current source

        if (recommendationEvent) {
          void trackAmplitudeEvent(recommendationEvent);
        }

        if (!this.preSaleId) {
          const response = await submitRecommendationForm({
            preSaleBusinessInformationInSchema: this.getRecommendationData,
          });

          this.preSaleId = response.data.data.id;
        } else {
          const payload = {
            ...this.getRecommendationData,
            available_number:
              this.flowType === "A" ? this.selectedNumber?.phone_number : null,
          };

          const hasHeaders = this.globalId && !authStore.isAuthenticated;

          const apiConfig: CustomAxiosRequestConfig = {
            headers: {
              ...(hasHeaders ? { "Pressone-X-Global-ID": this.globalId } : {}),
            },
            accessLevel: authStore.isAuthenticated ? "private" : "public",
          };

          await updateRecommendationForm(
            {
              preSaleBusinessInformationUpdateSchema: payload,
              presaleId: this.preSaleId,
            },
            apiConfig
          );
        }
      } catch (error) {
        handleError({
          error,
          fallbackMessage:
            "An error occurred while signing up. Please try again",
        });
      } finally {
        this.isLoading = false;
      }
    },
    resetStore() {
      const presaleId = this.preSaleId; // preserve presaleid so as to be able to use it to edit
      const globalId = this.globalId;
      this.$reset();
      this.preSaleId = presaleId; // bring it back
      this.globalId = globalId; // bring it back
    },
  },

  persist: PINIA_PERSIST_OPTIONS,
});
